/**
 * Dedicated on-device MediaPipe Face Landmarker for exam head pose.
 * Independent of WebGazer — runs on the exam #cameraVideo element.
 */

import {
  FaceLandmarker,
  FilesetResolver,
  type FaceLandmarkerResult,
} from '@mediapipe/tasks-vision';
import {
  isHeadPoseAwayFromCamera,
  yawPitchFromFacialMatrix,
  yawPitchFromNormalizedLandmarks,
  type HeadPoseDegrees,
} from './facialTransformPose';

const TASKS_VISION_VERSION = '0.10.21';
const WASM_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${TASKS_VISION_VERSION}/wasm`;
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

/** Target detection rate — do not gate on video.currentTime (often stuck on webcams). */
const MIN_DETECT_INTERVAL_MS = 66; // ~15 fps

export type HeadPoseListener = (pose: HeadPoseDegrees | null) => void;

let landmarker: FaceLandmarker | null = null;
let rafId: number | null = null;
let videoEl: HTMLVideoElement | null = null;
let listener: HeadPoseListener | null = null;
let latestPose: HeadPoseDegrees | null = null;
let lastDetectAt = 0;
let lastTimestampMs = 0;
let starting: Promise<void> | null = null;
let running = false;

export function getLatestLandmarkerHeadPose(): HeadPoseDegrees | null {
  return latestPose;
}

export function isLandmarkerHeadPoseAway(): boolean {
  return isHeadPoseAwayFromCamera(latestPose);
}

export function isFaceLandmarkerRunning(): boolean {
  return running && landmarker != null && videoEl != null;
}

async function ensureLandmarker(): Promise<FaceLandmarker> {
  if (landmarker) return landmarker;
  const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
  try {
    landmarker = await FaceLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URL,
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numFaces: 1,
      outputFacialTransformationMatrixes: true,
      outputFaceBlendshapes: false,
    });
  } catch {
    landmarker = await FaceLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URL,
        delegate: 'CPU',
      },
      runningMode: 'VIDEO',
      numFaces: 1,
      outputFacialTransformationMatrixes: true,
      outputFaceBlendshapes: false,
    });
  }
  return landmarker;
}

function poseFromResult(result: FaceLandmarkerResult): HeadPoseDegrees | null {
  const matrices = result.facialTransformationMatrixes;
  if (matrices?.length) {
    const raw = matrices[0];
    const data =
      raw && typeof raw === 'object' && 'data' in raw
        ? (raw as { data: ArrayLike<number> }).data
        : (raw as unknown as ArrayLike<number>);
    const fromMatrix = yawPitchFromFacialMatrix(data);
    if (fromMatrix && (Math.abs(fromMatrix.yaw) > 1 || Math.abs(fromMatrix.pitch) > 1)) {
      return fromMatrix;
    }
    // Near-zero matrix can still be wrong under mirror/cam — fall through to landmarks.
    if (fromMatrix && !result.faceLandmarks?.length) {
      return fromMatrix;
    }
  }

  const lm = result.faceLandmarks?.[0];
  if (lm?.length) {
    return yawPitchFromNormalizedLandmarks(lm);
  }
  return null;
}

function tick(): void {
  if (!running || !landmarker || !videoEl) {
    rafId = null;
    return;
  }

  const now = performance.now();
  const video = videoEl;

  if (video.readyState >= 2 && now - lastDetectAt >= MIN_DETECT_INTERVAL_MS) {
    lastDetectAt = now;
    // MediaPipe requires strictly increasing timestamps (ms).
    const ts = Math.max(now, lastTimestampMs + 1);
    lastTimestampMs = ts;
    try {
      const result = landmarker.detectForVideo(video, ts);
      latestPose = poseFromResult(result);
      listener?.(latestPose);
    } catch (err) {
      console.warn('[QuizSecure] Face Landmarker detect failed:', err);
      latestPose = null;
      listener?.(null);
    }
  }

  rafId = window.requestAnimationFrame(tick);
}

export async function startFaceLandmarkerHeadPose(
  video: HTMLVideoElement,
  onPose: HeadPoseListener
): Promise<void> {
  listener = onPose;
  videoEl = video;

  if (starting) {
    await starting;
  } else if (!landmarker) {
    starting = ensureLandmarker()
      .then(() => undefined)
      .catch((err) => {
        console.error('[QuizSecure] Face Landmarker failed to load:', err);
        landmarker = null;
        throw err;
      })
      .finally(() => {
        starting = null;
      });
    await starting;
  }

  if (!landmarker) {
    throw new Error('Face Landmarker unavailable');
  }

  running = true;
  lastDetectAt = 0;
  if (rafId == null) {
    rafId = window.requestAnimationFrame(tick);
  }
  console.info('[QuizSecure] Face Landmarker head-pose loop started');
}

export function stopFaceLandmarkerHeadPose(): void {
  running = false;
  listener = null;
  videoEl = null;
  latestPose = null;
  lastDetectAt = 0;
  if (rafId != null) {
    window.cancelAnimationFrame(rafId);
    rafId = null;
  }
}

export async function disposeFaceLandmarkerHeadPose(): Promise<void> {
  stopFaceLandmarkerHeadPose();
  try {
    landmarker?.close();
  } catch {
    /* ignore */
  }
  landmarker = null;
}
